"use client";

import React, { useState } from "react";
import { Check, Minus, MessageCircle, Mail, Users, PhoneCall, HardDrive, MonitorSmartphone, MessageSquare, ShieldCheck, Headset, Lock, Settings, Plug, type LucideIcon } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
import rawData from "./avstreamPlans.json";
interface PlanLimits {
  users: string;
  calls: string;
  storage: string;
}
interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
  badge: string | null;
  limits: PlanLimits;
  bullets: string[];
}
interface Addon {
  name: string;
  price: string;
  isNew: boolean;
  description: string;
  plans: string[];
}
interface Service {
  icon: string;
  title: string;
  text: string;
}
interface TableRowData {
  label: string;
  level?: number;
  v: string | string[];
}
interface TableGroup {
  title: string;
  rows: TableRowData[];
}
interface AvStreamData {
  source?: string;
  currency?: string;
  period?: string;
  plans: Plan[];
  addons: Addon[];
  services: Service[];
  groups: TableGroup[];
}
const data = rawData as AvStreamData;
const serviceIconMap: Record<string, LucideIcon> = {
  users: Users,
  devices: MonitorSmartphone,
  message: MessageSquare,
  shield: ShieldCheck,
  headset: Headset,
  lock: Lock,
  settings: Settings,
  plug: Plug
};
export default function AvStreamTiersComparison() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const renderCellContent = (v: string | string[], planIndex: number) => {
    if (typeof v === "string") {
      const char = v[planIndex] || "N";
      if (char === "Y") {
        return <Check className="size-5 text-primary mx-auto" />;
      }
      return <Minus className="size-5 text-muted-foreground/30 mx-auto" />;
    }
    const val = v[planIndex];
    if (val === "N") {
      return <Minus className="size-5 text-muted-foreground/30 mx-auto" />;
    }
    if (val === "Y") {
      return <Check className="size-4 sm:size-5 text-primary mx-auto" />;
    }
    return <span className="font-medium text-foreground text-[11px] sm:text-xs leading-snug break-words whitespace-normal inline-block">
        {val}
      </span>;
  };
  return <section id="avstream-tiers-comparison" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Intro */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Planes CloudUCM de Grandstream
          </h2>
          <p className="text-lg text-muted-foreground">
            Elige el plan de central telefónica en la nube que mejor se adapte al tamaño de tu empresa, desde pequeñas oficinas hasta grandes corporativos. Precios en USD por año.
          </p>
        </div>

        {/* 1. Grid of 5 plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20 justify-center">
          {data.plans.map((plan, index) => {
          const isLastOnLg = index === 4;
          const isFourthOnLg = index === 3;
          return <Card key={plan.id} className={cn("flex flex-col border-border shadow-sm hover:shadow-md transition-shadow relative",
          // For 5 cards in lg:grid-cols-3, center the bottom 2 items nicely on large screens
          isFourthOnLg && "lg:col-start-1 lg:translate-x-0 xl:col-start-auto", isLastOnLg && "md:col-span-2 lg:col-span-1")}>
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-2xl font-bold font-default text-foreground">
                      {plan.name}
                    </CardTitle>
                    {plan.badge && <Badge variant="default" className="text-xs font-semibold shrink-0">
                        {plan.badge}
                      </Badge>}
                  </div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold text-foreground tracking-tight">
                      {plan.price}
                    </span>
                    {plan.period && <span className="text-muted-foreground font-medium text-base">
                        {plan.period}
                      </span>}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col pt-0 pb-6 gap-5">
                  {/* Limits */}
                  <div className="space-y-2.5 rounded-md bg-muted/40 p-3.5 border border-border/50 text-sm">
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <Users className="size-4 text-primary" />
                        Usuarios / extensiones:
                      </span>
                      <span className="font-semibold text-foreground">{plan.limits.users}</span>
                    </div>
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <PhoneCall className="size-4 text-primary" />
                        Llamadas concurrentes:
                      </span>
                      <span className="font-semibold text-foreground">{plan.limits.calls}</span>
                    </div>
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <HardDrive className="size-4 text-primary" />
                        Almacenamiento (nube):
                      </span>
                      <span className="font-semibold text-foreground">{plan.limits.storage}</span>
                    </div>
                  </div>

                  {/* Bullets */}
                  {plan.bullets.length > 0 && <ul className="space-y-2 text-sm text-muted-foreground flex-1">
                      {plan.bullets.map((bullet, idx) => <li key={idx} className="flex items-start gap-2.5">
                          <Check className="size-4 text-primary shrink-0 mt-0.5" />
                          <span className="text-foreground/90">{bullet}</span>
                        </li>)}
                    </ul>}
                </CardContent>

                <CardFooter className="pt-0">
                  <Button variant="outline" className="w-full font-semibold hover:bg-primary hover:text-primary-foreground transition-colors" onClick={() => setIsModalOpen(true)}>
                    Seleccionar
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>

        {/* 2. Full plans comparison table */}
        <div className="mb-20">
          <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
                Comparación completa de planes
              </h3>
            </div>
            <p className="text-xs text-muted-foreground md:hidden">
              Desliza horizontalmente para ver todos los planes →
            </p>
          </div>

          <Card className="border-border shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="w-full min-w-[650px] table-fixed">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[170px] sm:w-[200px] md:w-[220px] p-2.5 sm:p-3 align-bottom sticky left-0 z-20 bg-background border-r border-border shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)] overflow-hidden whitespace-normal break-words">
                      <span className="text-xs sm:text-sm font-semibold font-default text-foreground block">
                        Características
                      </span>
                    </TableHead>
                    {data.plans.map((plan, i) => <TableHead key={plan.id} data-index={i} className="p-2 sm:p-2.5 text-center align-bottom bg-muted/10 whitespace-normal break-words">
                        <div className="flex flex-col items-center justify-end gap-0.5">
                          <span className="text-sm sm:text-base font-bold font-default text-foreground leading-tight break-words">
                            {plan.name}
                          </span>
                          <span className="text-xs font-semibold text-muted-foreground leading-tight break-words">
                            {plan.price}
                            {plan.period ? ` ${plan.period}` : ""}
                          </span>
                        </div>
                      </TableHead>)}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {data.groups.map((group, groupIdx) => <React.Fragment key={groupIdx}>
                      {/* Full-width Category Row */}
                      <TableRow className="bg-muted/60 hover:bg-muted/60 border-y border-border">
                        <TableCell colSpan={data.plans.length + 1} className="py-2.5 px-3 sm:px-4 font-bold font-default text-foreground text-xs sm:text-sm tracking-wide whitespace-normal break-words">
                          {group.title}
                        </TableCell>
                      </TableRow>

                      {/* Group feature rows */}
                      {group.rows.map((row, rowIdx) => <TableRow key={`${groupIdx}-${rowIdx}`} data-index={rowIdx} className="hover:bg-muted/20">
                          <TableCell className={cn("py-2 sm:py-2.5 px-2.5 sm:px-3 font-medium sticky left-0 z-10 bg-background border-r border-border shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)] leading-snug whitespace-normal break-words overflow-hidden", row.level === 1 ? "pl-5 sm:pl-7 text-muted-foreground font-normal text-[11px] sm:text-xs" : "text-foreground text-xs")}>
                            {row.label}
                          </TableCell>

                          {data.plans.map((plan, planIdx) => <TableCell key={`${plan.id}-${groupIdx}-${rowIdx}`} data-index={planIdx} className="text-center py-2 sm:py-2.5 px-1 sm:px-2 align-middle whitespace-normal break-words">
                              {renderCellContent(row.v, planIdx)}
                            </TableCell>)}
                        </TableRow>)}
                    </React.Fragment>)}

                  {/* Final row with Seleccionar buttons */}
                  <TableRow className="hover:bg-transparent border-t border-border">
                    <TableCell className="p-2.5 sm:p-3 sticky left-0 z-10 bg-background border-r border-border shadow-[2px_0_5px_-2px_rgba(0,0,0,0.06)] overflow-hidden" />
                    {data.plans.map((plan, i) => <TableCell key={`cta-${plan.id}`} data-index={i} className="p-2 sm:p-2.5 text-center align-middle whitespace-normal">
                        <Button variant="outline" size="sm" className="w-full px-1.5 py-1 text-xs font-semibold hover:bg-primary hover:text-primary-foreground transition-colors h-8" onClick={() => setIsModalOpen(true)}>
                          Seleccionar
                        </Button>
                      </TableCell>)}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>

        {/* 3. NEW block "Servicios incluidos" */}
        <div className="mb-20">
          <div className="mb-8 text-center max-w-3xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground mb-2">
              Servicios incluidos
            </h3>
            <p className="text-base text-muted-foreground">
              Todo lo que ofrece CloudUCM, la central telefónica en la nube de Grandstream.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.services.map((service, index) => {
            const IconComponent = serviceIconMap[service.icon] || ShieldCheck;
            return <Card key={index} className="flex flex-col border-border shadow-sm hover:shadow-md transition-shadow bg-card">
                  <CardHeader className="pb-3">
                    <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-3">
                      <IconComponent className="size-6" />
                    </div>
                    <CardTitle className="text-base font-bold font-default text-foreground leading-snug">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.text}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* 4. Block "Adicionales" */}
        <div>
          <div className="mb-6 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold font-default text-foreground">
                Adicionales
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Potencia tu plan con complementos según crezca tu operación.
              </p>
            </div>
            <span className="text-xs font-semibold text-muted-foreground bg-muted px-2.5 py-1 rounded">
              Precios en USD por año
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.addons.map((addon, index) => <Card key={index} className="flex flex-col border-border shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2 min-h-[48px]">
                    <CardTitle className="text-base font-bold font-default text-foreground leading-snug">
                      {addon.name}
                    </CardTitle>
                    {addon.isNew && <Badge variant="default" className="text-xs font-semibold shrink-0">
                        Nuevo
                      </Badge>}
                  </div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-foreground">
                      {addon.price}
                    </span>
                    <span className="text-xs text-muted-foreground font-medium">
                      / año
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col justify-between pt-0 pb-6 gap-4">
                  <p className="text-sm text-muted-foreground">
                    {addon.description}
                  </p>

                  <div>
                    <span className="block text-xs font-semibold text-foreground/80 mb-2">
                      Aplica a:
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {addon.plans.map(planName => <Badge key={planName} variant="outline" className="text-xs px-2 py-0.5 border-border text-foreground font-normal">
                          {planName}
                        </Badge>)}
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </div>

      {/* Contact modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-default">¿Cómo deseas continuar?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Elige el método de contacto que prefieras para recibir asesoría personalizada sobre tu plan seleccionado.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <Button asChild className="w-full py-6 text-lg" onClick={() => setIsModalOpen(false)}>
              <Link to="#">
                <Mail className="mr-2 size-5" />
                Formulario de Contacto
              </Link>
            </Button>
            <Button variant="outline" className="w-full py-6 text-lg border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20" asChild onClick={() => setIsModalOpen(false)}>
              <Link to="https://wa.me/1234567890" newTab>
                <MessageCircle className="mr-2 size-5" />
                Contactar por WhatsApp
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>;
}